import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Package, DollarSign, GraduationCap, ShieldCheck, TrendingUp, Store, ArrowRight, MapPin, UserPlus, Truck, HandCoins, Zap } from "lucide-react";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/lib/brand";

export default function Home() {
  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center grain overflow-hidden">
        <img src={BRAND.heroDually} alt="XD FREIGHT dually hotshot truck with gooseneck trailer on a Houston highway" className="absolute inset-0 w-full h-full object-cover animate-kenburns" />
        <div className="absolute inset-0 bg-gradient-to-r from-xd-bg via-xd-bg/85 to-xd-bg/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-xd-bg via-transparent to-transparent" />
        {/* live highway light streaks */}
        <div aria-hidden="true" className="absolute inset-0 overflow-hidden pointer-events-none">
          <span className="animate-streak absolute h-px" style={{ top: "30%", left: 0, width: "30rem", animationDuration: "6.5s", background: "linear-gradient(90deg, transparent, rgba(245,179,1,0.6), transparent)", boxShadow: "0 0 14px rgba(245,179,1,0.6)" }} />
          <span className="animate-streak absolute h-px" style={{ top: "55%", left: 0, width: "40rem", animationDuration: "9s", animationDelay: "2s", background: "linear-gradient(90deg, transparent, rgba(46,107,255,0.7), transparent)", boxShadow: "0 0 14px rgba(46,107,255,0.7)" }} />
          <span className="animate-streak absolute h-px" style={{ top: "73%", left: 0, width: "24rem", animationDuration: "7.5s", animationDelay: "3.4s", background: "linear-gradient(90deg, transparent, rgba(245,179,1,0.5), transparent)", boxShadow: "0 0 12px rgba(245,179,1,0.5)" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 w-full">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-xd-gold/15 border border-xd-gold/30 text-xd-gold px-3 py-1 text-xs font-bold uppercase tracking-wide">
              <MapPin size={13} /> Houston, TX · Freight & Logistics
            </span>
            <h1 className="mt-5 font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
              Find loads.<br/>Move freight.<br/><span className="text-xd-gold">Manage your business.</span>
            </h1>
            <p className="mt-6 text-lg text-xd-muted max-w-xl">XD Freight is building one technology platform connecting carriers, shippers, freight opportunities, and the essential transportation business services companies need to grow.</p>
            <p className="mt-3 text-sm text-xd-muted/80 max-w-xl">Specializing in hotshot and expedited transportation with a growing network serving the broader freight industry.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Button asChild data-testid="hero-find-loads-btn" className="h-13 min-h-[54px] px-8 bg-xd-blue hover:bg-xd-blueH text-white font-bold text-base rounded-xl">
                <Link to="/find-loads"><Search size={18} className="mr-2" /> Find Loads</Link>
              </Button>
              <Button asChild data-testid="hero-ship-btn" variant="outline" className="h-13 min-h-[54px] px-8 border-white/25 bg-white/5 backdrop-blur text-white hover:bg-white/10 font-bold text-base rounded-xl">
                <Link to="/ship"><Package size={18} className="mr-2" /> Ship With XD</Link>
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-xd-muted">
              <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-xd-green" /> USDOT 3994079 · MC 1499189 · Active Motor Carrier</span>
            </div>
            <p className="mt-3 text-xs text-xd-dim uppercase tracking-wide">Hotshot · Expedited · Flatbed · Dry Van · Power Only · Box Truck · More</p>
          </motion.div>
        </div>
      </section>

      {/* Status strip */}
      <section className="border-y border-xd-blue/25 bg-xd-blue/[0.07]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-x-4 gap-y-1 text-center sm:text-left">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-xd-blue shrink-0"><Zap size={14} /> Now live</span>
          <p className="text-sm text-xd-muted">Carrier onboarding, AI dispatch, saved lanes and business tools are live today. Full live load booking &amp; brokerage are rolling out — join now to get set up first.</p>
        </div>
      </section>

      {/* PLATFORM */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs uppercase tracking-[0.25em] font-bold text-xd-blue">One platform. Built for freight.</span>
          <h2 className="mt-3 font-heading text-4xl sm:text-5xl font-bold tracking-tight">Everything the freight ecosystem needs</h2>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          <PlatformCard icon={Truck} title="For Carriers" desc="Find freight opportunities and manage more of your operation from one place." cta="Explore Carrier Tools" to="/for-carriers" />
          <PlatformCard icon={Package} title="For Shippers" desc="Request transportation and connect with capacity for your freight." cta="Ship With XD" to="/ship" />
          <PlatformCard icon={Store} title="Marketplace" desc="Discover products and services designed to help transportation businesses operate and grow." cta="Explore Marketplace" to="/marketplace" />
        </div>
      </section>

      {/* SIMPLE 3-STEP */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">Simple from load to paid</h2>
          <p className="mt-4 text-xd-muted text-lg">No clutter, no runaround. A few steps and you're rolling.</p>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          <Step n="1" icon={UserPlus} title="Join & get verified" desc="Create your account and complete a quick 3-step setup. Our team verifies you fast." />
          <Step n="2" icon={Search} title="Find your freight" desc="Browse loads by equipment, lane, weight and rate — with your rate-per-mile up front." />
          <Step n="3" icon={HandCoins} title="Move it & manage it" desc="Get dispatched, upload your POD, and track revenue and payments in one place." />
        </div>
        <div className="mt-10 text-center">
          <Button asChild data-testid="steps-join-btn" className="h-12 px-8 bg-xd-gold hover:bg-xd-goldH text-xd-bg font-bold rounded-xl">
            <Link to="/register">Get Started Free <ArrowRight size={18} className="ml-2" /></Link>
          </Button>
        </div>
      </section>

      {/* Bento */}
      <section className="border-y border-xd-border bg-xd-paper/40">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <h2 className="font-heading text-4xl font-bold tracking-tight max-w-xl">Everything hotshot. One platform.</h2>
          <div className="mt-12 grid gap-6 md:grid-cols-12">
            <BentoCard className="md:col-span-7" icon={Search} title="For Carriers" desc="Get AI-matched to freight, request dispatch, track shipments, upload PODs and run your business from your phone." to="/for-carriers" tall image={BRAND.heroFleet} />
            <BentoCard className="md:col-span-5" icon={Package} title="For Shippers" desc="Request a quote and XD's team responds directly. Simple and human." to="/ship" />
            <BentoCard className="md:col-span-4" icon={Store} title="XD Marketplace" desc="Fuel, factoring, insurance, trucks & trailers — vetted partners." to="/marketplace" />
            <BentoCard className="md:col-span-4" icon={DollarSign} title="XD Money" desc="Revenue, rate-per-mile, expenses & profit in one dashboard." to="/dashboard/money" />
            <BentoCard className="md:col-span-4" icon={GraduationCap} title="XD Academy" desc="Learn the hotshot business start to scale." to="/academy" />
          </div>
        </div>
      </section>

      {/* Why XD */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <h2 className="font-heading text-4xl font-bold tracking-tight">Why carriers choose XD</h2>
        <div className="mt-12 grid gap-8 md:grid-cols-4">
          <Why icon={Truck} title="Built for your rig" desc="Dually hotshots, goosenecks, flatbeds and full freight — not just big rigs." />
          <Why icon={ShieldCheck} title="Verified & trusted" desc="Every carrier is XD-reviewed, so shippers and carriers move with confidence." />
          <Why icon={TrendingUp} title="Real business tools" desc="XD Money, XD Score and smart load matching — beyond a plain load list." />
          <Why icon={Store} title="One place for it all" desc="Loads, money, docs, funding and partners together — simple." />
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="relative overflow-hidden rounded-3xl border border-xd-border grain">
          <img src={BRAND.heroFleet} alt="XD FREIGHT fleet in Houston" className="absolute inset-0 w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-xd-bg via-xd-bg/90 to-xd-bg/40" />
          <div className="relative p-10 sm:p-16 max-w-xl">
            <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tight">Join the XD FREIGHT network</h2>
            <p className="mt-4 text-xd-muted text-lg">Get verified, find loads, and run your hotshot business like a pro.</p>
            <Button asChild data-testid="cta-join-btn" className="mt-8 h-13 min-h-[54px] px-8 bg-xd-blue hover:bg-xd-blueH text-white font-bold rounded-xl">
              <Link to="/register">Join XD FREIGHT <ArrowRight size={18} className="ml-2" /></Link>
            </Button>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

function Step({ n, icon: Icon, title, desc }) {
  return (
    <div className="relative bg-xd-paper border border-xd-border rounded-2xl p-7 hover:border-xd-blue/50 transition-colors">
      <span className="absolute -top-4 left-7 grid place-items-center w-9 h-9 rounded-full bg-xd-gold text-xd-bg font-heading font-black">{n}</span>
      <span className="grid place-items-center w-12 h-12 rounded-xl bg-xd-blue/15 text-xd-blue mt-2"><Icon size={24} /></span>
      <h3 className="mt-4 font-heading text-xl font-bold">{title}</h3>
      <p className="mt-2 text-sm text-xd-muted">{desc}</p>
    </div>
  );
}

function PlatformCard({ icon: Icon, title, desc, cta, to }) {
  return (
    <div className="group bg-xd-paper border border-xd-border rounded-2xl p-7 flex flex-col hover:border-xd-blue/50 transition-colors">
      <span className="grid place-items-center w-12 h-12 rounded-xl bg-xd-blue/15 text-xd-blue"><Icon size={24} /></span>
      <h3 className="mt-4 font-heading text-2xl font-bold">{title}</h3>
      <p className="mt-2 text-sm text-xd-muted flex-1">{desc}</p>
      <Button asChild data-testid={`platform-${title.toLowerCase().replace(/\s+/g, "-")}`} className="mt-6 bg-xd-surface hover:bg-xd-blue text-white font-semibold rounded-xl">
        <Link to={to}>{cta} <ArrowRight size={16} className="ml-2" /></Link>
      </Button>
    </div>
  );
}

function BentoCard({ className, icon: Icon, title, desc, to, tall, image }) {
  return (
    <Link to={to} data-testid={`bento-${title.toLowerCase().replace(/\s+/g, "-")}`}
      className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-6 flex flex-col justify-end hover:border-xd-gold/50 transition-colors ${tall ? "min-h-[320px]" : "min-h-[220px]"} ${className}`}>
      {image && <><img src={image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-45 transition-opacity" /><div className="absolute inset-0 bg-gradient-to-t from-xd-bg via-xd-bg/70 to-transparent" /></>}
      <div className="relative">
        <span className="grid place-items-center w-11 h-11 rounded-xl bg-xd-blue/20 text-xd-blue mb-4"><Icon size={22} /></span>
        <h3 className="font-heading text-2xl font-bold">{title}</h3>
        <p className="mt-2 text-sm text-xd-muted">{desc}</p>
        <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-xd-gold group-hover:gap-2 transition-all">Explore <ArrowRight size={15} /></span>
      </div>
    </Link>
  );
}

function Why({ icon: Icon, title, desc }) {
  return (
    <div>
      <span className="grid place-items-center w-12 h-12 rounded-xl bg-xd-surface border border-xd-border text-xd-gold"><Icon size={24} /></span>
      <h3 className="mt-4 font-heading text-xl font-bold">{title}</h3>
      <p className="mt-2 text-sm text-xd-muted">{desc}</p>
    </div>
  );
}
